import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getBlogPosts } from "../services/api";

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const postsData = await getBlogPosts();
        // Sort by date (newest first) and filter published posts
        const publishedPosts = postsData
          .filter(post => post.published !== false)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setBlogPosts(publishedPosts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Septic Installation": "bg-blue-100 text-blue-800",
      "Maintenance & Repair": "bg-orange-100 text-orange-800",
      "Septic Education": "bg-green-100 text-green-800",
      "System Comparison": "bg-purple-100 text-purple-800",
      "Permits & Regulations": "bg-red-100 text-red-800",
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  return (
    <div>
      <SEO 
        title="Septic Installation Blog | Thunder Bay Tips & Guides"
        description="Expert septic installation tips, guides, and insights for Northwestern Ontario. Learn about Eljen systems, permits, maintenance, and more."
        keywords="septic blog, septic installation tips, Thunder Bay septic, septic system guide"
      />
      <Header />

      {/* Hero Section */}
      <section className="gh-intro pt-32 pb-16 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center" data-reveal>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Septic Installation Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert tips, guides, and insights for septic system installation,
              maintenance, and compliance in Northwestern Ontario.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  data-testid={`blog-card-${post.slug}`}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Image */}
                  {post.image && (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6">
                    {/* Category Badge */}
                    {post.category && (
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-3 ${
                          getCategoryColor(post.category)
                        }`}
                      >
                        {post.category}
                      </span>
                    )}

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span>{formatDate(post.date)}</span>
                      {post.readTime && <span>• {post.readTime}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;